import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { endTreatment, reactivateCase, triggerRefinement } from '../../../services/patient';
import { toApiError } from '../../../services/apiErrors';
import { hasActionRequired } from '../../../utils/constants';
import type { usePatientActions } from '../../../contexts/PatientActionsContext';

/**
 * The three Treatment actions on the patient Actions tab.
 *
 * They share one shape - guard on the loading flag, call, toast, invalidate,
 * release in `finally` - and the page had three near-identical copies of it.
 * The loading flag is deliberately held until the invalidations settle in all
 * three: each gates its own tile, and the predicates in actionAvailability.ts
 * read a status that stays stale until the refetch lands, so releasing early
 * would admit a second, non-idempotent call.
 */

// 403 / 404 / 409 all read as one generic failure otherwise, and 409 - the case
// moved while this page was open - is the one the user can act on.
function reactivateErrorMessage(error: unknown): string {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status === 409) return 'This case has already moved on - refreshing';
    if (status === 403) return 'You do not have access to reactivate this case';
    return toApiError(error).message || 'Failed to reactivate case';
}

type ToastKind = 'success' | 'error';

interface UseTreatmentActionsOptions {
    patientId: string | null | undefined;
    /** The patient's current `requiredAction`, for sticky-sort bookkeeping. */
    actionRequired: string | null | undefined;
    showToast: (message: string, kind: ToastKind) => void;
    markActionCompleted: (patientId: string, action: string) => void;
    /** Patient action handoffs (upload retention / refinement records). */
    actions: ReturnType<typeof usePatientActions>;
}

export interface TreatmentActions {
    handleEndTreatment: (retainerOption: string, reason?: string) => Promise<void>;
    isEndTreatmentLoading: boolean;
    isEndTreatmentModalOpen: boolean;
    setIsEndTreatmentModalOpen: (open: boolean) => void;

    handleTriggerRefinement: () => Promise<void>;
    isTriggerRefinementLoading: boolean;
    isTriggerRefinementModalOpen: boolean;
    setIsTriggerRefinementModalOpen: (open: boolean) => void;

    handleReactivateCase: () => Promise<void>;
    isReactivateCaseLoading: boolean;
    isReactivateCaseModalOpen: boolean;
    setIsReactivateCaseModalOpen: (open: boolean) => void;
}

export function useTreatmentActions({
    patientId,
    actionRequired,
    showToast,
    markActionCompleted,
    actions
}: UseTreatmentActionsOptions): TreatmentActions {
    const queryClient = useQueryClient();

    const [isEndTreatmentModalOpen, setIsEndTreatmentModalOpen] = useState(false);
    const [isEndTreatmentLoading, setIsEndTreatmentLoading] = useState(false);
    const [isTriggerRefinementModalOpen, setIsTriggerRefinementModalOpen] = useState(false);
    const [isTriggerRefinementLoading, setIsTriggerRefinementLoading] = useState(false);
    const [isReactivateCaseModalOpen, setIsReactivateCaseModalOpen] = useState(false);
    const [isReactivateCaseLoading, setIsReactivateCaseLoading] = useState(false);

    const refetchPatient = useCallback(async () => {
        await queryClient.invalidateQueries({ queryKey: ['patient', patientId] });
        await queryClient.invalidateQueries({ queryKey: ['patients'] });
    }, [queryClient, patientId]);

    /** Sticky-sort the row only if it genuinely had a pending action: these are
     *  gated by internalCaseStatus, not requiredAction, so they are clickable
     *  during normal monitoring when requiredAction is 'None'. */
    const recordCompletion = useCallback(() => {
        if (patientId && hasActionRequired(actionRequired)) {
            markActionCompleted(patientId, actionRequired);
        }
    }, [patientId, actionRequired, markActionCompleted]);

    const handleEndTreatment = useCallback(
        async (retainerOption: string, reason?: string) => {
            if (!patientId || isEndTreatmentLoading) return;

            setIsEndTreatmentLoading(true);
            try {
                await endTreatment(patientId, retainerOption, reason);
                recordCompletion();
                showToast('Treatment ended successfully', 'success');
                await refetchPatient();
                setIsEndTreatmentModalOpen(false);

                const isOutsideOdo = retainerOption === 'outside_odo';
                actions.openUploadRetention(patientId, isOutsideOdo ? 'congratulations' : 'intro');
            } catch {
                showToast('Failed to end treatment', 'error');
            } finally {
                setIsEndTreatmentLoading(false);
            }
        },
        [patientId, isEndTreatmentLoading, recordCompletion, showToast, refetchPatient, actions]
    );

    const handleTriggerRefinement = useCallback(async () => {
        if (!patientId || isTriggerRefinementLoading) return;

        setIsTriggerRefinementLoading(true);
        try {
            try {
                await triggerRefinement(patientId);
            } catch {
                showToast('Failed to trigger refinement', 'error');
                return;
            }

            recordCompletion();
            setIsTriggerRefinementModalOpen(false);
            showToast('Refinement triggered successfully', 'success');
            // Handoff runs before the invalidations so the follow-up modal appears
            // immediately rather than after the refetches settle.
            await actions.handleAction('Upload refinement records', patientId);
            await refetchPatient();
        } finally {
            setIsTriggerRefinementLoading(false);
        }
    }, [
        patientId,
        isTriggerRefinementLoading,
        recordCompletion,
        showToast,
        refetchPatient,
        actions
    ]);

    // Confirmed like its two neighbours. Reactivation takes no operator input -
    // the backend derives its own reason from the status being left - but it is
    // worth a confirmation because what it does is easy to mispredict: the case
    // re-enters redesign rather than resuming where it stopped, and the practice
    // has no way to undo it.
    const handleReactivateCase = useCallback(async () => {
        if (!patientId || isReactivateCaseLoading) return;

        setIsReactivateCaseLoading(true);
        try {
            let result;
            try {
                result = await reactivateCase(patientId);
            } catch (error) {
                // A 409 means the case already moved - ODO staff can reactivate it
                // from the work queue at the same time. Refetch on failure so the
                // tile reflects reality instead of inviting an identical retry.
                showToast(reactivateErrorMessage(error), 'error');
                await queryClient.invalidateQueries({ queryKey: ['patient', patientId] });
                return;
            }

            // The transition committed either way; the mirrored revision event is
            // the part that can fail on its own. Saying so beats a clean success
            // on a case whose Revisions panel will show no reason for the reopen.
            if (result.conversationMirrorFailed) {
                showToast('Case reactivated, but the reason could not be recorded', 'error');
            } else {
                showToast('Case reactivated successfully', 'success');
            }
            setIsReactivateCaseModalOpen(false);
            await refetchPatient();
        } finally {
            setIsReactivateCaseLoading(false);
        }
    }, [patientId, isReactivateCaseLoading, showToast, refetchPatient, queryClient]);

    return {
        handleEndTreatment,
        isEndTreatmentLoading,
        isEndTreatmentModalOpen,
        setIsEndTreatmentModalOpen,
        handleTriggerRefinement,
        isTriggerRefinementLoading,
        isTriggerRefinementModalOpen,
        setIsTriggerRefinementModalOpen,
        handleReactivateCase,
        isReactivateCaseLoading,
        isReactivateCaseModalOpen,
        setIsReactivateCaseModalOpen
    };
}
